import { useState, useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  VISIT_EVENT_TYPES,
  SIREN_ICON_ID,
  SUBTYPE_PRE_CONSULTATION,
  SUBTYPE_POST_CONSULTATION,
  STATUS_SESSION_CANCELLED,
  BOOKING_STATUSES,
  BOOKING_PAYMENT_STATUSES,
} from 'src/consumer/constants'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { prepareWaitingTimeStringFromBookedSlot } from '../../utils'
import Payment from '@mui/icons-material/Payment'
import { useTheme, useMediaQuery } from '@mui/material'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'

const PatientInfo = ({ bookedSlot, apptCalendarSettings, forMentor, styles, clinicAssociationsData, showClinicIndicator, openPaymentLinksDialog, isBlockedSlot = false, isSlotBookedInOtherClinic = false, clinicAssociations = null }) => {
  const theme = useTheme()
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [sirenIconError, setSirenIconError] = useState(false)
  const containerRef = useRef(null)
  const [containerHeight, setContainerHeight] = useState(null)
  
  // REQUIREMENT: Based on height try to accommodate meaningful info
  // Measure container height to determine what info to show
  useEffect(() => {
    if (containerRef.current) {
      const updateHeight = () => {
        const height = containerRef.current?.offsetHeight || 0
        setContainerHeight(height)
      }
      updateHeight()
      const resizeObserver = new ResizeObserver(updateHeight)
      resizeObserver.observe(containerRef.current)
      return () => resizeObserver.disconnect()
    }
  }, [])
  
  // Height-based visibility: Show less critical info only if there's enough space
  // Critical info (always shown): Name, Patient Number, Age/Gender, Visit Number
  // Tertiary info (show if height > 60px): Consultation type tags, Waiting time, etc.
  const hasMinHeightForTertiaryInfo = containerHeight === null || containerHeight > 60
  
  // Get patient data from multiple possible locations
  // According to Clinic-Assistant-Booked-Event.md:
  // - Patient details on bookedEvents (patientNumber, userId, userName) may be REDACTED (null)
  //   when booking belongs to a clinic-brand the assistant does not manage
  // - patientInfo array contains patient details when accessPatientDetails = true
  // Priority: patientInfo fields override booked event fields (which may be redacted)
  const patientNumber = bookedSlot?.patientInfo?.patientNumber || bookedSlot?.patientNumber || bookedSlot?.bookedEvent?.patientNumber || bookedSlot?.bookedEventInfo?.patientNumber
  // Age and gender: check patientInfo first (from patientInfo array), then patient object, then booked event
  const patientDateOfBirth = bookedSlot?.patientInfo?.dateOfBirth || bookedSlot?.patient?.dateOfBirth
  const patientGender = bookedSlot?.patientInfo?.gender || bookedSlot?.patient?.gender
  const patientAge = bookedSlot?.patientInfo?.age || bookedSlot?.patient?.age
  
  // REQUIREMENT: Show age/gender wherever available (always show when available)
  const showAgeAndGender = patientDateOfBirth || patientGender || patientAge
  const showWaitingTime = apptCalendarSettings?.showWaitingTime && hasMinHeightForTertiaryInfo

  // Check if payment is pending
  const isPaymentPending =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  return (
    <div ref={containerRef} className="d-flex flex-column gap-1 w-100">
      {/* Payment Pending Badge - Highest priority */}
      {isPaymentPending && (
        <div 
          style={{ 
            fontSize: '11px',
            fontWeight: 700,
            color: '#7C2D12',
            backgroundColor: '#FED7AA',
            padding: '3px 8px',
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            width: 'fit-content',
            border: '1.5px solid #FB923C',
            boxShadow: '0 1px 3px rgba(251, 146, 60, 0.3)',
          }}
        >
          <Payment sx={{ fontSize: 12, color: '#EA580C' }} />
          <span>PAYMENT PENDING</span>
        </div>
      )}

      {/* REQUIREMENT: Show clinic shortName for other clinic location cards for mentors */}
      {isSlotBookedInOtherClinic && forMentor && (() => {
        // Get clinic shortName from various sources
        let clinicShortName = null
        if (clinicAssociations && Array.isArray(clinicAssociations) && bookedSlot?.clinicId) {
          const matchingAssociation = clinicAssociations.find(
            (assoc) => assoc?.id === bookedSlot?.clinicId || 
                       assoc?.clinicId === bookedSlot?.clinicId ||
                       assoc?.clinicBrandId === bookedSlot?.clinicBrandId
          )
          if (matchingAssociation && matchingAssociation?.clinic && Array.isArray(matchingAssociation.clinic)) {
            const clinicLocation = matchingAssociation.clinic.find(
              (loc) => loc?.id === bookedSlot?.clinicId || loc?.clinicId === bookedSlot?.clinicId
            )
            clinicShortName = clinicLocation?.shortName || clinicLocation?.short_name || 
                             clinicLocation?.nickName || clinicLocation?.nickname ||
                             (matchingAssociation.clinic.length === 1 ? (matchingAssociation.clinic[0]?.shortName || matchingAssociation.clinic[0]?.short_name || matchingAssociation.clinic[0]?.nickName || matchingAssociation.clinic[0]?.nickname) : null)
          }
        }
        // Fallback to bookedSlot clinic data
        if (!clinicShortName) {
          clinicShortName = bookedSlot?.clinic?.shortName || bookedSlot?.clinic?.short_name ||
                           bookedSlot?.clinicLocation?.shortName || bookedSlot?.clinicLocation?.short_name ||
                           bookedSlot?.clinicLocation?.nickName || bookedSlot?.clinicLocation?.nickname ||
                           bookedSlot?.clinicLocationData?.shortName || bookedSlot?.clinicLocationData?.short_name ||
                           bookedSlot?.clinicLocationData?.nickName || bookedSlot?.clinicLocationData?.nickname ||
                           clinicAssociationsData?.clinicLocationShortName
        }
        
        if (clinicShortName) {
          return (
            <div 
              style={{ 
                fontSize: '11px',
                fontWeight: 700,
                color: '#F59E0B',
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                padding: '3px 8px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                width: 'fit-content',
                border: '1.5px solid #F59E0B',
                boxShadow: '0 1px 3px rgba(245, 158, 11, 0.3)',
              }}
            >
              🏥 {clinicShortName}
            </div>
          )
        }
        return null
      })()}

      {/* Delayed appointment warning */}
      {bookedSlot?.wasDelayedBecauseOfOverlaps && (
        <div 
          style={{ 
            fontSize: '11px',
            fontWeight: 500,
            color: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            padding: '2px 6px',
            borderRadius: '4px',
            display: 'inline-block',
            width: 'fit-content',
          }}
        >
          ⏰ Actual Start: {dayjs.utc(bookedSlot?.startTime).local().format('hh:mm A')}
        </div>
      )}

      {/* Main patient information */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        alignItems: 'center', 
        gap: isNarrowScreen ? '4px' : '6px', 
        position: 'relative',
        minWidth: 0, // Allow truncation
        overflow: 'hidden', // Prevent overflow
      }}>
        {/* Emergency indicator */}
        {bookedSlot?.visitEventType === VISIT_EVENT_TYPES.emergency && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: '#dc2626',
              color: '#fff',
              padding: isNarrowScreen ? '2px 5px' : '3px 8px',
              borderRadius: '4px',
              fontSize: isNarrowScreen ? '9px' : '10px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              flexShrink: 0, // Don't shrink emergency indicator
              zIndex: 10,
              position: 'relative',
              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
            }}
          >
            {sirenIconError ? (
              <span style={{ marginRight: isNarrowScreen ? '2px' : '4px', fontSize: isNarrowScreen ? '12px' : '14px' }}>🚨</span>
            ) : (
              <img
                src={getImageLink(SIREN_ICON_ID)}
                alt="emergency"
                onError={() => setSirenIconError(true)}
                style={{
                  height: isNarrowScreen ? '12px' : '14px',
                  width: isNarrowScreen ? '12px' : '14px',
                  marginRight: isNarrowScreen ? '2px' : '4px',
                  filter: 'brightness(0) invert(1)',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            )}
            Emergency
          </span>
        )}
        
        {/* Patient number, name, age/gender, and screening info - primary info - MUST be visible */}
        {isNarrowScreen ? (
          // Mobile: Stack name and age/gender vertically or in compact format
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '2px',
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
          }}>
            {/* Name and Patient Number */}
            <span 
              style={{ 
                fontSize: '12px', 
                fontWeight: 600, 
                color: styles.fontColor || '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                lineHeight: '1.3',
              }}
              title={bookedSlot?.bookedEventInfo?.userName || bookedSlot?.bookedEvent?.userName || bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Unknown'}
            >
              {patientNumber && (
                <span style={{ opacity: 0.9, marginRight: '3px', flexShrink: 0, fontWeight: 700, color: styles.fontColor || '#1F2937' }}>
                  #{patientNumber}
                </span>
              )}
              {patientNumber && <span style={{ color: styles.fontColor || '#1F2937' }}>•</span>}
              <span style={{ marginLeft: patientNumber ? '3px' : '0', color: styles.fontColor || '#1F2937', fontWeight: 700 }}>
                {bookedSlot?.bookedEventInfo?.userName || bookedSlot?.bookedEvent?.userName || bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Unknown'}
              </span>
            </span>
            {/* Age and Gender on separate line */}
            {showAgeAndGender && (
              <span style={{ 
                fontSize: '10px',
                fontWeight: 500,
                opacity: 0.9,
                color: styles.fontColor || '#1F2937', // Ensure color is visible
                lineHeight: '1.2',
              }}>
                {patientDateOfBirth && getAgeFromDobWithTime(patientDateOfBirth)}
                {patientAge && !patientDateOfBirth && `${patientAge}Y`}
                {(patientDateOfBirth || patientAge) && patientGender && ' / '}
                {patientGender && patientGender?.[0]?.toUpperCase()}
              </span>
            )}
          </div>
        ) : (
          // Desktop: Original inline format
          <span 
            style={{ 
              fontSize: '13px', 
              fontWeight: 600, 
              color: styles.fontColor || '#fff',
              flex: '1 1 auto',
              minWidth: 0, // Allow truncation
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
            title={bookedSlot?.bookedEventInfo?.userName || bookedSlot?.bookedEvent?.userName || bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Unknown'} // Show full name on hover
          >
            {patientNumber && (
              <span style={{ opacity: 0.9, marginRight: '4px', flexShrink: 0, fontWeight: 700, color: styles.fontColor || '#1F2937' }}>
                #{patientNumber}
              </span>
            )}
            {patientNumber && <span style={{ color: styles.fontColor || '#1F2937' }}>•</span>}
            <span style={{ marginLeft: '4px', color: styles.fontColor || '#1F2937', fontWeight: 700 }}>
              {bookedSlot?.bookedEventInfo?.userName || bookedSlot?.bookedEvent?.userName || bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Unknown'}
            </span>
            {/* Age and gender inline next to name */}
            {showAgeAndGender && (
              <span style={{ 
                marginLeft: '6px', 
                fontSize: '11px',
                fontWeight: 500,
                opacity: 0.85,
                color: styles.fontColor || '#1F2937', // Ensure color is visible
              }}>
                (
                {patientDateOfBirth && getAgeFromDobWithTime(patientDateOfBirth)}
                {patientAge && !patientDateOfBirth && `${patientAge}Y`}
                {(patientDateOfBirth || patientAge) && patientGender && ', '}
                {patientGender && patientGender?.[0]?.toUpperCase()}
                )
              </span>
            )}
          </span>
        )}

        {/* Screening Info - Show if available */}
        {(() => {
          const screeningInfo = (bookedSlot?.previousSteps || [])?.find(
            (item) => item?.refType === 'screening' && item?.status === 'completed',
          )
          if (!screeningInfo?.stepHandlerName) return null
          
          return (
            <span
              style={{
                fontSize: '9px',
                fontWeight: 600,
                color: '#059669',
                backgroundColor: 'rgba(5, 150, 105, 0.15)',
                padding: '2px 6px',
                borderRadius: '4px',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                border: '1px solid rgba(5, 150, 105, 0.3)',
              }}
              title={`Screened by ${screeningInfo.stepHandlerName}`}
            >
              🩺 {screeningInfo.stepHandlerName}
            </span>
          )
        })()}

        {/* Visit Number - REQUIREMENT: Show visit number whenever available (always show) */}
        {bookedSlot?.patientMeetingSeqNumber && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#8b5cf6',
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              padding: '2px 6px',
              borderRadius: '4px',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            {bookedSlot.patientMeetingSeqNumber}
            <sup style={{ fontSize: '8px', lineHeight: 1 }}>
              {getOrdinalSuffixForSuperScript(bookedSlot.patientMeetingSeqNumber)}
            </sup>
          </span>
        )}

        {/* Consultation type tags - hide on narrow screens and if height is limited */}
        {hasMinHeightForTertiaryInfo && bookedSlot?.eventSubType === SUBTYPE_PRE_CONSULTATION && (
          <span
            style={{
              fontSize: '9px',
              fontWeight: 600,
              color: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              padding: '2px 6px',
              borderRadius: '4px',
              flexShrink: 0,
            }}
            className="d-none d-sm-inline-block" // Hide on xs, show on sm+
          >
            Pre-Rx
          </span>
        )}
        {hasMinHeightForTertiaryInfo && bookedSlot?.eventSubType === SUBTYPE_POST_CONSULTATION && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              color: '#8b5cf6',
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              padding: '2px 6px',
              borderRadius: '4px',
              flexShrink: 0,
            }}
            className="d-none d-sm-inline-block" // Hide on xs, show on sm+
          >
            Post-Rx
          </span>
        )}

        {/* Cancelled status - always show as it's critical info */}
        {bookedSlot?.status === STATUS_SESSION_CANCELLED && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#dc2626',
              backgroundColor: 'rgba(220, 38, 38, 0.15)',
              padding: '2px 6px',
              borderRadius: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Cancelled
          </span>
        )}

        {/* Payment Link tag - for blocked slots with payment pending - MUST be visible */}
        {isBlockedSlot && 
         bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
         bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING &&
         openPaymentLinksDialog && (
          <span
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              if (openPaymentLinksDialog) {
                openPaymentLinksDialog()
              }
            }}
            style={{
              fontSize: '9px',
              fontWeight: 600,
              color: '#4338ca',
              backgroundColor: 'rgba(67, 56, 202, 0.15)',
              padding: '2px 6px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              border: '1px solid rgba(67, 56, 202, 0.3)',
              transition: 'all 0.2s ease',
              flexShrink: 0, // Never shrink payment link
              whiteSpace: 'nowrap', // Prevent wrapping
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(67, 56, 202, 0.25)'
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(67, 56, 202, 0.15)'
              e.currentTarget.style.transform = 'scale(1)'
            }}
            title="View Payment Links"
          >
            <Payment sx={{ fontSize: '11px' }} />
            {!isNarrowScreen && (
              <span>Payment</span>
            )}
          </span>
        )}

        {/* Payment mode tag */}
        {!isBlockedSlot && bookedSlot?.feePaymentMode === 'aumhum' && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#ffffff',
              backgroundColor: 'rgba(245, 158, 11, 0.9)',
              padding: '3px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(245, 158, 11, 1)',
              boxShadow: '0 1px 3px rgba(245, 158, 11, 0.4)',
              letterSpacing: '0.3px',
            }}
          >
            Paid on AUMHUM
          </span>
        )}

        {/* Waiting time - hide on narrow screens and if height is limited */}
        {showWaitingTime && hasMinHeightForTertiaryInfo && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 500,
              color: styles.fontColor 
                ? (styles.fontColor === '#FFFFFF' || styles.fontColor === '#fff' || styles.fontColor === 'white'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : 'rgba(31, 41, 55, 0.85)')
                : 'rgba(31, 41, 55, 0.85)',
              fontStyle: 'italic',
              flexShrink: 0,
            }}
            className="d-none d-md-inline-block" // Hide on xs/sm, show on md+
          >
            {forMentor
              ? bookedSlot?.createdTime && prepareWaitingTimeStringFromBookedSlot(bookedSlot)
              : `Booked: ${dayjs.utc(bookedSlot?.createdTime).local()?.format('h:mm a')}`}
          </span>
        )}

        {/* Shifted position indicator - hide on narrow screens and if height is limited */}
        {hasMinHeightForTertiaryInfo && bookedSlot?.wasDelayedBecauseOfOverlaps && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 500,
              color: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              padding: '2px 6px',
              borderRadius: '4px',
              flexShrink: 0,
            }}
            className="d-none d-sm-inline-block" // Hide on xs, show on sm+
          >
            Shifted
          </span>
        )}
      </div>

      {/* Related booking event - REMOVED: No need to show Related Consultation on the strip on calendar */}
      {/* {bookedSlot?.triggerBookedEventDetails?.bookedEventId && (
        <div style={{ fontSize: '12px' }}>
          Related {bookedSlot?.triggerBookedEventDetails?.bookedEventType}:{' '}
          {bookedSlot?.triggerBookedEventDetails?.bookedEventMentorName} (
          {dayjs(bookedSlot?.triggerBookedEventDetails?.bookedEventStartTime).format(
            'MMM DD, YYYY',
          )}
          )
        </div>
      )} */}
    </div>
  )
}

export default PatientInfo
